import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { WorkOrderLineDto } from '../../../services/api';
import { Button } from '../../../components/ui/button';

export interface BoxEntry {
  workOrderLineId: string;
  partida: string;
  bultos: number;
  quantity: number;
}

interface Props {
  orderLines: WorkOrderLineDto[];
  onClose: () => void;
  onSubmit: (entry: BoxEntry) => Promise<void>;
  /** Si se pasa, el panel entra en modo edición y precarga valores. */
  initial?: BoxEntry;
}

/**
 * Diálogo lateral para añadir/editar una caja de un palet. Flujo: producto →
 * partida → bultos → kg → Enter.
 *
 * En modo *añadir* limpia los campos tras guardar para encadenar otra caja.
 * En modo *editar* cierra el panel tras guardar. Esc cierra.
 */
export const AddBoxPanel: React.FC<Props> = ({ orderLines, onClose, onSubmit, initial }) => {
  const isEdit = !!initial;
  const [productId, setProductId] = useState<string>(initial?.workOrderLineId ?? orderLines[0]?.id ?? '');
  const [partida, setPartida] = useState(initial?.partida ?? '');
  const [bultos, setBultos] = useState<string>(initial ? String(initial.bultos) : '');
  const [kg, setKg] = useState<string>(initial ? initial.quantity.toFixed(2) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productRef = useRef<HTMLSelectElement>(null);
  const partidaRef = useRef<HTMLInputElement>(null);
  const bultosRef = useRef<HTMLInputElement>(null);
  const kgRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // En edición damos foco a partida (lo más habitual de cambiar);
    // en alta, al selector de producto.
    if (isEdit) partidaRef.current?.focus();
    else productRef.current?.focus();
  }, [isEdit]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const selectedLine = useMemo(
    () => orderLines.find(l => l.id === productId),
    [orderLines, productId],
  );

  const submit = async () => {
    setError(null);
    if (!productId) { setError('Selecciona un producto.'); return; }
    if (!partida.trim()) { setError('Falta la partida.'); partidaRef.current?.focus(); return; }
    const b = Math.floor(Number(bultos) || 0);
    const k = Number(kg) || 0;
    if (b <= 0 && k <= 0) { setError('Indica bultos o kg.'); bultosRef.current?.focus(); return; }

    try {
      setSaving(true);
      await onSubmit({ workOrderLineId: productId, partida: partida.trim(), bultos: b, quantity: k });
      if (isEdit) {
        onClose();
      } else {
        // Encadenar otra caja
        setPartida('');
        setBultos('');
        setKg('');
        productRef.current?.focus();
      }
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>, next?: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (next) next();
      else submit();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[26rem] h-full bg-white shadow-xl flex flex-col">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-900">{isEdit ? 'Editar caja' : 'Añadir caja'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Cerrar">
            <X size={20} />
          </button>
        </header>

        <div className="p-4 space-y-4 flex-1 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Producto</label>
            <select
              ref={productRef}
              value={productId}
              onChange={e => setProductId(e.target.value)}
              onKeyDown={e => handleKey(e, () => partidaRef.current?.focus())}
              className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              {orderLines.map(l => (
                <option key={l.id} value={l.id}>
                  {l.productName} — pedido {l.quantity} {l.unit ?? ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Partida</label>
            <input
              ref={partidaRef}
              type="text"
              value={partida}
              onChange={e => setPartida(e.target.value)}
              onKeyDown={e => handleKey(e, () => bultosRef.current?.focus())}
              placeholder="Ej. PG-1234"
              maxLength={50}
              className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-lg font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bultos</label>
              <input
                ref={bultosRef}
                type="number"
                min={0}
                step={1}
                value={bultos}
                onChange={e => setBultos(e.target.value)}
                onKeyDown={e => handleKey(e, () => kgRef.current?.focus())}
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-2xl text-right tabular-nums focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kg</label>
              <input
                ref={kgRef}
                type="number"
                min={0}
                step={0.01}
                value={kg}
                onChange={e => setKg(e.target.value)}
                onKeyDown={e => handleKey(e)}
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-2xl text-right tabular-nums focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {selectedLine && (
            <p className="text-sm text-slate-500">
              Pedido: <strong>{selectedLine.quantity} {selectedLine.unit ?? ''}</strong>
            </p>
          )}

          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</p>
          )}
        </div>

        <footer className="p-4 border-t border-slate-200 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cerrar (Esc)</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
            {saving ? 'Guardando…' : isEdit ? 'Guardar (Enter)' : 'Añadir (Enter)'}
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};
